import { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { AdvancedMarker, APIProvider, Map as GoogleMap, Pin } from "@vis.gl/react-google-maps";
import { useTranslation } from "react-i18next";
import api_key from "../../../env";
import "./map.scss";
import OutlinedPinkButton from "../../buttons/outlinedPinkButton/OutlinedPinkButton";
import ENV from "../../../env";
const DEFAULT_LATITUDE = 50.840329972;
const DEFAULT_LONGITUDE = 4.3499986;

function Map({ marker, setMarker, address, defaultZoom, minHeight, maxHeight }) {
  const { t } = useTranslation("map");
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const inputRef = useRef(null);

  const center = {
    lat: marker?.lat || marker?.latitude || DEFAULT_LATITUDE,
    lng: marker?.lng || marker?.longitude || DEFAULT_LONGITUDE,
  };

  const handleExpandMap = () => {
    setIsMapExpanded(!isMapExpanded);
  };

  async function getAddressFromLatLon(lat, lon) {
    const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lon}&key=${api_key.google_maps_api_key}`;
    let address = "";

    try {
      const response = await fetch(geocodeUrl);
      const data = await response.json();

      if (data.results && data.results.length > 0) {
        address = data.results[0].formatted_address;
      } else {
        address = t("address_not_found");
      }
    } catch (error) {
      console.error(t("error_fetching_address"), error);
      address = t("error_fetching_address");
    }

    return address;
  }

  async function handleNewMarker(lat, lng, address) {
    const newAddress = address || (await getAddressFromLatLon(lat, lng));
    setMarker({
      latitude: lat,
      longitude: lng,
      latitudeDelta: 0.05,
      longitudeDelta: 0.05,
      address: newAddress,
    });

    if (inputRef.current) {
      inputRef.current.value = newAddress;
    }
  }

  useEffect(() => {
    if (window?.google?.maps?.places && inputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ["geocode"],
        fields: ["geometry", "formatted_address"],
      });

      const listener = autocomplete.addListener("place_changed", async () => {
        const place = autocomplete.getPlace();

        if (place.geometry) {
          await handleNewMarker(
            place.geometry.location.lat(),
            place.geometry.location.lng(),
            place.formatted_address,
          );
        }
      });

      return () => {
        listener.remove();
      };
    }
  }, [window?.google?.maps?.places]);

  return (
    <section>
      <p className="sectionTitle">{t("location")}</p>
      <div className="mapContainer">
        <div className="locationInformationsContainer">
          <div className="locationInformations">
            <p>{t("event_location")}</p>
            <input
              type="text"
              ref={inputRef}
              placeholder={t("search_address")}
              className="autocompleteInput"
              defaultValue={address}
            />
          </div>
          <div className="locationButtons">
            <OutlinedPinkButton onPressFunction={handleExpandMap}>
              {isMapExpanded ? t("shrink_map") : t("expand_map")}
            </OutlinedPinkButton>
          </div>
        </div>

        <div className="map" style={{ height: isMapExpanded ? maxHeight : minHeight }}>
          <APIProvider apiKey={api_key.google_maps_api_key} libraries={["places"]}>
            <GoogleMap
              defaultZoom={defaultZoom}
              mapId={ENV.mapId}
              center={center}
              options={{ disableDefaultUI: true }}
              onClick={(event) => {
                handleNewMarker(event.detail.latLng.lat, event.detail.latLng.lng);
              }}
            >
              <AdvancedMarker
                position={{
                  lat: marker?.lat || marker?.latitude || DEFAULT_LATITUDE,
                  lng: marker?.lng || marker?.longitude || DEFAULT_LONGITUDE,
                }}
              >
                <Pin />
              </AdvancedMarker>
            </GoogleMap>
          </APIProvider>
        </div>
      </div>
    </section>
  );
}

Map.defaultProps = {
  defaultZoom: 10,
  minHeight: 400,
  maxHeight: 1000,
};

Map.propTypes = {
  marker: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
  }).isRequired,
  setMarker: PropTypes.func.isRequired,
  address: PropTypes.string,
  defaultZoom: PropTypes.number,
  minHeight: PropTypes.number,
  maxHeight: PropTypes.number,
};

export default Map;
